import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';

import lang from '../../../lib/language';
import { CountryData } from '../../../lib/models/enroll';
import { RootState } from '../../../lib/store';
import { getCountryList as getCountryListCall } from '../../../lib/store/crm';
import { CRMCall } from '../../../lib/store/crm/types';
import CountriesCache from '../../../lib/store-util/CountriesCache';
import { isCallStatusError, isCallStatusReady } from '../../../lib/util/error-handling/StatusByCallHelpers';
import {
  getFirstNameValidity,
  getGenericFieldValidity,
  getLastNameValidity,
  validateControl,
} from '../../../lib/util/ValidationSchemes/Validate';

import { CountryControl, InputControl } from '../../../components';
import { ICountryControlOptions } from '../../../components/CountryControl/CountryControl.types';
import EnrollmentState from '../../../store-util/EnrollmentState';
import { UI_ROUTES } from '../../../util/routes';
import Layout from '../component/Layout';

import styles from './EnrollPersonalInfo.module.scss';

type Props = ReduxProps;
const ADDITIONAL_HEIGHT_PER_ERROR = 24;

const EnrollPersonalInfo = (props: Props): JSX.Element => {
  const { getCountryList, isServicesReady, isCountryListReady, isGetCountryListFailed } = props;
  const isGetCountryListPending = !isCountryListReady && isGetCountryListFailed;
  const [additionalHeight, setAdditionalHeight] = useState(0);
  const countries = CountriesCache.use();
  const { form } = EnrollmentState.use();
  const { firstName, lastName, country } = form;
  const history = useHistory();

  const [isSubmitButtonClicked, setIsSubmitButtonClicked] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  useEffect(() => {
    if (!countries && !isGetCountryListPending && isServicesReady) {
      getCountryList();
    }
  }, [countries, isServicesReady, isGetCountryListPending, getCountryList]);

  const countryOptions = useMemo((): ICountryControlOptions[] => countries
    ?.filter((countryData: CountryData) => countryData.id && countryData.name)
    .sort((a: CountryData, b: CountryData) => a.name.localeCompare(b.name))
    .map((countryData: CountryData) => ({
      label: countryData.name,
      value: countryData.id,
    })), [countries]);

  const validateForm = useCallback(() => {
    const countryErrors = validateControl(country, [], lang.commonOnboardingInitialCountryLabel(), true).errors;
    const firstNameErrors = getFirstNameValidity(firstName).errors;
    const lastNameErrors = getLastNameValidity(lastName).errors;
    return {
      countryErrors,
      firstNameErrors,
      lastNameErrors,
      isValid: !countryErrors.length && !firstNameErrors.length && !lastNameErrors.length,
    };
  }, [country, firstName, lastName]);

  const handleSubmit = () => {
    setIsSubmitButtonClicked(true);
    setHasUserInteracted(true);

    const { countryErrors, firstNameErrors, lastNameErrors, isValid } = validateForm();

    if (isValid) {
      EnrollmentState.setPersonalInfoStepPassed(true);
      history.push(UI_ROUTES.secondEnroll);
    }

    // Calculate how many fields have errors, we only display one at a time
    const totalErrorFields = [
      countryErrors.length > 0,
      firstNameErrors.length > 0,
      lastNameErrors.length > 0,
    ].filter(Boolean).length;

    // Update the state with the new additional height
    setAdditionalHeight(totalErrorFields * ADDITIONAL_HEIGHT_PER_ERROR);
  };

  useEffect(() => {
    if (hasUserInteracted) { // Only run this if user has interacted
      const { countryErrors, firstNameErrors, lastNameErrors } = validateForm();

      const totalErrorFields = [
        countryErrors.length > 0,
        firstNameErrors.length > 0,
        lastNameErrors.length > 0,
      ].filter(Boolean).length;

      setAdditionalHeight(totalErrorFields * ADDITIONAL_HEIGHT_PER_ERROR);
    }
  }, [country, firstName, lastName, validateForm, hasUserInteracted]);

  return (
    <Layout
      formTitle={lang.commonEnrollmentCreateProfileTitle()}
      formSubtitle={lang.commonEnrollmentCreateProfileSubTitle()}
      onSubmit={handleSubmit}
      onBackBtnPress={() => {
        EnrollmentState.resetForm();
        history.goBack();
      }}
      nextButtonText={lang.commonContinueButton()}
      displayFooter
      displayLoginButton
      displayNextButton
      displayBackButton
      additionalHeight={additionalHeight}
    >
      <div className={classNames(styles.row, { [styles.errorRow]: isSubmitButtonClicked
        && validateControl(country, [], lang.commonOnboardingGenericRequiredField(), true).errors.length > 0 })}
      >
        <span className={styles.title}>{lang.commonOnboardingPhoneVerificationCountry()}</span>

        <CountryControl
          className={styles.input}
          name=""
          isRequired
          options={countryOptions}
          onSubmit={handleSubmit}
          value={country}
          id="countryResidence"
          ariaLabel={lang.commonOnboardingInitialCountryLabel()}
          placeholder={lang.commonOnboardingInitialCountryLabel()}
          onCountryChange={(countryId: string) => {
            EnrollmentState.setCountry(countryId);
          }}
          errors={
            isSubmitButtonClicked
              ? validateControl(country, [], lang.commonOnboardingGenericRequiredField(), true).errors
              : []
          }
        />
      </div>

      <div className={classNames(styles.row, { [styles.errorRow]: isSubmitButtonClicked
        && getGenericFieldValidity(firstName).errors.length > 0 })}
      >
        <span className={styles.title}>{lang.commonOnboardingPersonalDetailsFirstName()}</span>

        <InputControl
          className={styles.input}
          name=""
          placeholder=""
          onSubmit={handleSubmit}
          label={lang.commonEnrollmentEnterFirstName()}
          value={firstName}
          onValueChange={(value: string | number) => {
            EnrollmentState.setFirstName(String(value));
          }}
          errors={isSubmitButtonClicked ? getFirstNameValidity(firstName).errors : []}
        />
      </div>

      <div className={classNames(styles.row, { [styles.errorRow]: isSubmitButtonClicked
        && getGenericFieldValidity(lastName).errors.length > 0 })}
      >
        <span className={styles.title}>{lang.commonOnboardingPersonalDetailsLastName()}</span>
        <InputControl
          className={styles.input}
          name=""
          placeholder=""
          onSubmit={handleSubmit}
          label={lang.commonEnrollmentEnterLastName()}
          value={lastName}
          onValueChange={(value: string | number) => {
            EnrollmentState.setLastName(String(value));
          }}
          errors={isSubmitButtonClicked ? getLastNameValidity(lastName).errors : []}
        />
      </div>
    </Layout>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  getCountryList: () => dispatch(getCountryListCall()),
});

const mapStateToProps = (state: RootState) => ({
  isServicesReady: state.app.isServicesReady,
  isCountryListReady: isCallStatusReady(CRMCall.getCountryList, state.crm.statusByCall),
  isGetCountryListFailed: isCallStatusError(CRMCall.getCountryList, state.crm.statusByCall),
});
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(EnrollPersonalInfo);
